import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, CheckCircle2, ClipboardCheck, ExternalLink, FileText, Image as ImageIcon,
    Loader2, Package, Trash2, Wrench, History as HistoryIcon, Activity, Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { JobPartsPanel } from "@/components/JobPartsPanel";
import { BillingTab } from "@/components/BillingTab";
import { DiagnosticsTab } from "@/components/DiagnosticsTab";
import { JobInspectionPanel } from "@/components/JobInspectionPanel";
import { Textarea } from "@/components/ui/textarea";
import { LiveTimer } from "@/components/LiveTimer";
import { StageTimeline } from "@/components/StageTimeline";
import { TATBar } from "@/components/TATBar";
import { Lightbox } from "@/components/Lightbox";
import { AttachmentDropzone } from "@/components/AttachmentDropzone";
import { CopyLinkButton } from "@/components/CopyLinkButton";
import { JobCardAPI, AttachmentAPI, extractError, buildSignedUrl, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime, formatDuration } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS_PILL = {
    booked: "bg-zinc-100 text-zinc-700",
    in_progress: "bg-sky-100 text-sky-800",
    awaiting_approval: "bg-violet-100 text-violet-800",
    awaiting_parts: "bg-blue-100 text-blue-800",
    qc: "bg-indigo-100 text-indigo-800",
    ready: "bg-emerald-100 text-emerald-800",
    delivered: "bg-emerald-200 text-emerald-900",
    cancelled: "bg-rose-100 text-rose-700",
};

export default function JobDetailPage() {
    const { id } = useParams();
    const { user, permissions } = useAuth();
    const [job, setJob] = useState(null);
    const [tat, setTat] = useState(null);
    const [logs, setLogs] = useState({ items: [], totals_seconds_by_user: {} });
    const [atts, setAtts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const [transitionNotes, setTransitionNotes] = useState("");
    const [odoOut, setOdoOut] = useState("");
    const [activeTab, setActiveTab] = useState("overview");

    const canApprove = permissions.includes("jobcard:approve");
    const canAttach = permissions.includes("jobcard:attach");
    const canTransition = permissions.includes("jobcard:transition");

    const publicUrl = job?.customer_status_token ? `${window.location.origin}/j/${job.customer_status_token}` : "";
    const apiOriginUrl = useMemo(() => API_BASE.replace(/\/api$/, ""), []);

    async function refresh() {
        setLoading(true);
        try {
            const [j, t, l, a] = await Promise.all([
                JobCardAPI.get(id),
                JobCardAPI.tat(id),
                JobCardAPI.timeLogs(id),
                AttachmentAPI.list(id),
            ]);
            setJob(j); setTat(t); setLogs(l); setAtts(a.items || []);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    }

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

    async function transition(toStatus) {
        if (!job) return;
        if (job.status === "ready" && toStatus === "delivered" && !odoOut && !job.odometer_out) {
            toast.error("Capture odometer-out before delivering.");
            return;
        }
        setActing(true);
        try {
            const body = { to: toStatus, notes: transitionNotes || undefined, version: job.version };
            if (toStatus === "delivered") body.odometer_out = Number(odoOut || job.odometer_out);
            await JobCardAPI.transition(id, body);
            toast.success(`Moved → ${toStatus}`);
            setTransitionNotes(""); setOdoOut("");
            refresh();
        } catch (e) { toast.error(extractError(e)); }
        finally { setActing(false); }
    }

    async function markApproved(nextStatus) {
        setActing(true);
        try {
            await JobCardAPI.markApproved(id, { next_status: nextStatus, version: job.version, notes: transitionNotes || undefined });
            toast.success("Approved");
            setTransitionNotes("");
            refresh();
        } catch (e) { toast.error(extractError(e)); }
        finally { setActing(false); }
    }

    async function removeAttachment(aid) {
        if (!confirm("Delete this attachment?")) return;
        try {
            await AttachmentAPI.remove(id, aid);
            setAtts((p) => p.filter((x) => x.id !== aid));
            toast.success("Deleted");
        } catch (e) { toast.error(extractError(e)); }
    }

    if (loading || !job) {
        return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;
    }

    const allowed = job.allowed_next_statuses || [];
    const isFinal = job.status === "delivered" || job.status === "cancelled";

    return (
        <div className="space-y-5 animate-fade-in" data-testid="job-detail-page">
            {/* Header */}
            <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <Link to="/jobs" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
                        <ArrowLeft className="h-3.5 w-3.5" /> Job cards
                    </Link>
                    <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{job.job_number}</h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={cn("rounded-md px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider", STATUS_PILL[job.status] || "bg-zinc-100 text-zinc-700")}>{job.status}</span>
                        <span className="text-sm text-zinc-500">·</span>
                        <span className="text-sm">{job.customer?.display_name}</span>
                        <span className="text-sm text-zinc-500">·</span>
                        <span className="font-mono text-sm">{job.vehicle?.registration_number}</span>
                        <span className="text-sm text-zinc-500">·</span>
                        <span className="text-sm">{job.vehicle?.make} {job.vehicle?.model}</span>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {publicUrl && <CopyLinkButton value={publicUrl} label="Copy customer link" testid="job-copy-public" />}
                    {publicUrl && (
                        <Button asChild variant="ghost" size="sm">
                            <a href={`/j/${job.customer_status_token}`} target="_blank" rel="noreferrer" data-testid="job-public-link">
                                Open <ExternalLink className="ml-1 h-3 w-3" />
                            </a>
                        </Button>
                    )}
                </div>
            </header>

            {/* Action bar */}
            {!isFinal && (
                <Card className="space-y-3 p-4" data-testid="job-action-bar">
                    <div className="flex flex-wrap items-center gap-2">
                        {canTransition && job.status === "in_progress" && (
                            <ActionButton onClick={() => transition("awaiting_approval")} disabled={acting} data-testid="job-action-transition-awaiting_approval">Request approval</ActionButton>
                        )}
                        {canApprove && job.status === "awaiting_approval" && (
                            <>
                                <ActionButton variant="primary" onClick={() => markApproved("in_progress")} disabled={acting} data-testid="job-action-mark-approved">
                                    <CheckCircle2 className="mr-1 h-4 w-4" /> Mark approved
                                </ActionButton>
                                <ActionButton onClick={() => markApproved("awaiting_parts")} disabled={acting} data-testid="job-action-mark-approved-parts">Approve → parts</ActionButton>
                            </>
                        )}
                        {canTransition && allowed.filter((s) => s !== "awaiting_approval" && s !== "cancelled" && s !== "delivered").map((s) => (
                            <ActionButton key={s} onClick={() => transition(s)} disabled={acting} data-testid={`job-action-transition-${s}`}>{labelFor(s)}</ActionButton>
                        ))}
                        {canTransition && allowed.includes("delivered") && (
                            <div className="flex items-center gap-1">
                                <Input className="h-8 w-28" type="number" placeholder="odo out" value={odoOut}
                                       onChange={(e) => setOdoOut(e.target.value)} data-testid="action-odo-out" />
                                <ActionButton variant="primary" onClick={() => transition("delivered")} disabled={acting} data-testid="job-action-transition-delivered">Deliver</ActionButton>
                            </div>
                        )}
                        {canTransition && user.role !== "technician" && (
                            <ActionButton variant="danger" onClick={() => transition("cancelled")} disabled={acting} data-testid="job-action-cancel">Cancel</ActionButton>
                        )}
                    </div>
                    <Textarea
                        rows={2}
                        placeholder="Optional notes for this transition (visible in history)…"
                        value={transitionNotes}
                        onChange={(e) => setTransitionNotes(e.target.value)}
                        data-testid="transition-notes"
                    />
                </Card>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="techtime" data-testid="tab-techtime"><Wrench className="mr-1 h-3 w-3" />Time</TabsTrigger>
                    <TabsTrigger value="parts" data-testid="job-parts-tab"><Package className="mr-1 h-3 w-3" />Parts</TabsTrigger>
                    <TabsTrigger value="billing" data-testid="billing-tab"><FileText className="mr-1 h-3 w-3" />Billing</TabsTrigger>
                    <TabsTrigger value="diagnostics" data-testid="dtc-tab">Diagnostics</TabsTrigger>
                    <TabsTrigger value="inspection" data-testid="inspection-tab"><ClipboardCheck className="mr-1 h-3 w-3" />Inspection</TabsTrigger>
                    <TabsTrigger value="attachments" data-testid="tab-attachments"><ImageIcon className="mr-1 h-3 w-3" />Attachments ({atts.length})</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history"><HistoryIcon className="mr-1 h-3 w-3" />History</TabsTrigger>
                    <TabsTrigger value="tat" data-testid="tab-tat"><Activity className="mr-1 h-3 w-3" />TAT</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Card className="p-4 space-y-3">
                            <h3 className="font-display text-sm font-bold tracking-tight uppercase text-zinc-500">Complaint</h3>
                            <p className="text-sm">{job.complaint_summary || "—"}</p>
                            <h3 className="font-display text-sm font-bold tracking-tight uppercase text-zinc-500">Inspection notes</h3>
                            <p className="text-sm">{job.inspection_notes || "—"}</p>
                        </Card>
                        <Card className="p-4">
                            <h3 className="font-display text-sm font-bold tracking-tight uppercase text-zinc-500">Snapshot</h3>
                            <dl className="mt-2 grid grid-cols-2 gap-y-2 text-sm">
                                <Item label="Opened" value={formatDateTime(job.opened_at)} />
                                <Item label="Closed" value={formatDateTime(job.closed_at)} />
                                <Item label="Odometer in" value={job.odometer_in ? `${job.odometer_in} km` : "—"} />
                                <Item label="Odometer out" value={job.odometer_out ? `${job.odometer_out} km` : "—"} />
                                <Item label="Approval req'd" value={job.approval_requested_at ? formatDateTime(job.approval_requested_at) : "—"} />
                                <Item label="QC failed" value={job.qc_failed_count > 0 ? `${job.qc_failed_count}×` : "0"} />
                                <Item label="Public token" value={job.customer_status_token} mono />
                            </dl>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="techtime">
                    <Card className="space-y-4 p-4">
                        <LiveTimer jobId={id} job={job} logs={logs} onChange={refresh} />
                    </Card>
                </TabsContent>

                <TabsContent value="parts">
                    <Card className="p-4">
                        <JobPartsPanel jobId={id} jobStatus={job?.status} />
                    </Card>
                </TabsContent>

                <TabsContent value="billing">
                    <BillingTab jobId={id} />
                </TabsContent>

                <TabsContent value="diagnostics">
                    <DiagnosticsTab job={job} />
                </TabsContent>

                <TabsContent value="inspection">
                    <JobInspectionPanel jobId={id} />
                </TabsContent>

                <TabsContent value="attachments" className="space-y-4">
                    {canAttach && !isFinal && (
                        <Card className="p-4"><AttachmentDropzone jobId={id} onUploaded={() => refresh()} /></Card>
                    )}
                    <Card className="p-4">
                        {atts.length === 0 ? (
                            <div className="grid h-32 place-items-center text-sm text-zinc-500">
                                <span className="flex items-center gap-2"><Camera className="h-4 w-4" /> No attachments yet</span>
                            </div>
                        ) : (
                            <AttachmentGrid items={atts} onOpen={setLightbox} onRemove={canAttach ? removeAttachment : null} />
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <Card className="p-4">
                        <StageTimeline stages={job.stage_history || []} durations={tat?.by_stage || {}} />
                    </Card>
                </TabsContent>

                <TabsContent value="tat">
                    <Card className="space-y-4 p-4" data-testid="tat-card">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display text-base font-bold tracking-tight">Turn-around time</h3>
                            <span className="font-mono text-sm text-zinc-500">
                                Total · {formatDuration((tat?.total) || 0)}
                            </span>
                        </div>
                        <TATBar byStage={tat?.by_stage} current={job.status} liveSeconds={tat?.seconds_in_current_stage} total={tat?.total} />
                        {tat?.qc_failed_count > 0 && (
                            <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-200">
                                QC failed {tat.qc_failed_count}× before passing.
                            </div>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>

            <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
        </div>
    );
}

function labelFor(s) {
    return {
        booked: "Booked", in_progress: "Start / Resume",
        awaiting_approval: "Request approval", awaiting_parts: "Awaiting parts",
        qc: "Send to QC", ready: "Mark ready", delivered: "Deliver", cancelled: "Cancel",
    }[s] || s;
}

function ActionButton({ children, variant = "default", ...rest }) {
    const cls = variant === "primary"
        ? "bg-brand-red text-white hover:bg-brand-redDeep"
        : variant === "danger"
            ? "border-rose-300 bg-white text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:bg-zinc-900 dark:text-rose-300 dark:hover:bg-rose-950/40"
            : "";
    return (
        <Button size="sm" variant={variant === "default" ? "outline" : "default"} className={cls} {...rest}>
            {children}
        </Button>
    );
}

function Item({ label, value, mono }) {
    return (
        <>
            <dt className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</dt>
            <dd className={cn("text-right text-sm", mono && "font-mono text-xs")}>{value || "—"}</dd>
        </>
    );
}

function AttachmentGrid({ items, onOpen, onRemove }) {
    const grouped = items.reduce((acc, a) => { (acc[a.kind || "other"] = acc[a.kind || "other"] || []).push(a); return acc; }, {});
    const order = ["before", "after", "damage", "inspection", "parts_invoice", "other"];
    return (
        <div className="space-y-5">
            {order.filter((k) => grouped[k]).map((k) => (
                <section key={k}>
                    <h4 className="mb-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">{k.replace("_", " ")} · {grouped[k].length}</h4>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {grouped[k].map((a) => <AttachmentTile key={a.id} a={a} onOpen={onOpen} onRemove={onRemove} />)}
                    </div>
                </section>
            ))}
        </div>
    );
}

function AttachmentTile({ a, onOpen, onRemove }) {
    const isImage = (a.category || "").startsWith("image");
    const isVideo = (a.category || "").startsWith("video");
    return (
        <div className="group relative overflow-hidden rounded-md border bg-zinc-50 dark:bg-zinc-900" data-testid="attachment-thumb">
            <button onClick={() => onOpen(a)} className="block w-full text-left" data-testid={`att-open-${a.id}`}>
                {isImage ? (
                    <img src={buildSignedUrl(a.signed_url)} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                ) : isVideo ? (
                    <div className="grid aspect-square place-items-center bg-zinc-900 text-white">
                        <Camera className="h-7 w-7" />
                    </div>
                ) : (
                    <div className="grid aspect-square place-items-center"><FileText className="h-7 w-7 text-zinc-400" /></div>
                )}
            </button>
            <div className="space-y-0.5 p-2 text-[11px]">
                <div className="truncate">{a.caption || a.original_filename}</div>
                <div className="flex items-center justify-between font-mono text-[10px] text-zinc-500">
                    <span>{a.kind}</span>
                    <span>{(a.size_bytes / 1024).toFixed(0)} KB</span>
                </div>
            </div>
            {onRemove && (
                <button
                    onClick={(e) => { e.stopPropagation(); onRemove(a.id); }}
                    className="absolute right-1 top-1 hidden rounded bg-white/95 p-1 text-rose-600 shadow group-hover:block dark:bg-zinc-900"
                    title="Delete"
                    data-testid={`att-del-${a.id}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}
